import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Avatar,
  useTheme,
  Dialog,
  IconButton,
} from "@mui/material";
import {
  EmojiEvents,
  School,
  Storefront,
  FitnessCenter,
  LocationOn,
  Verified,
  Close,
  Class as ClassIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
// === ایمپورت‌های Swiper ===
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Zoom } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/zoom";

import CommentsSection from "../../../../Components/CommentsSection"; // Adjust path if needed

export default function FullDetails({ coach, coachId }) {
  const theme = useTheme();
  const navigate = useNavigate();
  // کنترل مودال گالری
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [initialSlide, setInitialSlide] = useState(0);

  const galleryImages = coach?.images || [];
  const specialties = coach?.supportedSpecialties?.length
    ? coach.supportedSpecialties
    : [{ name: "فیتنس" }];
  const certifications = coach?.certifications || [];
  const coachName = coach?.fullname || "مربی ورزشی";

  // محاسبه مجموع باشگاه‌های ثبت شده سیستمی و دستی
  const totalGyms =
    (coach?.gymId?.length || 0) + (coach?.customGymNames?.length || 0);

  // مپ کردن انواع قیمت‌گذاری به فارسی
  const priceTypeMap = {
    regular: "عادی",
    private: "خصوصی",
    online: "آنلاین",
    group: "گروهی",
    home: "در منزل",
  };

  const handleOpenLightbox = (index) => {
    setInitialSlide(index);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
  };

  return (
    <Box
      component="main"
      sx={{ display: "flex", flexDirection: "column", gap: { xs: 2.5, md: 3 } }}
    >
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
      >
        {/* Bio Section */}
        <Paper
          component="section"
          sx={{
            p: { xs: 2, md: 2.5 },
            border: `1px solid rgba(255,255,255,0.05)`,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 4,
              height: "100%",
              bgcolor: "primary.main",
            }}
          />
          <Typography
            component="h2"
            variant="subtitle1"
            fontWeight={800}
            color="text.primary"
            sx={{ mb: 1.5 }}
          >
            درباره مربی
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            lineHeight={2}
            sx={{ whiteSpace: "pre-line" }}
          >
            {coach?.bio || "بیوگرافی ثبت نشده است."}
          </Typography>
        </Paper>

        {/* Highlights Section */}
        <Box
          component="section"
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
            gap: 1.5,
            mt: 3,
          }}
        >
          {[
            {
              icon: <EmojiEvents sx={{ color: "#FFC400", fontSize: 28 }} />,
              val: coach?.experienceYears || 0,
              label: "سال تجربه",
              bg: "rgba(255, 196, 0, 0.05)",
              border: "#FFC40040",
            },
            {
              icon: <School sx={{ color: "#00E5FF", fontSize: 28 }} />,
              val: certifications?.length || 0,
              label: "افتخار و مدرک",
              bg: "rgba(0, 229, 255, 0.05)",
              border: "#00E5FF40",
            },
            {
              icon: <Storefront sx={{ color: "#00E676", fontSize: 28 }} />,
              val: totalGyms, // استفاده از مجموع باشگاه‌ها
              label: "باشگاه فعال",
              bg: "rgba(0, 230, 118, 0.05)",
              border: "#00E67640",
            },
          ].map((stat, i) => (
            <Paper
              key={i}
              sx={{
                p: 1.5,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
                bgcolor: stat.bg,
                border: `1px solid ${stat.border}`,
                borderRadius: 3,
              }}
            >
              {stat.icon}
              <Typography variant="h6" fontWeight={800}>
                {stat.val}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {stat.label}
              </Typography>
            </Paper>
          ))}
        </Box>

        {/* Gallery Section */}
        {galleryImages?.length > 0 && (
          <Paper
            component="section"
            sx={{
              p: 2,
              bgcolor: "background.paper",
              border: `1px solid rgba(255, 255, 255, 0.05)`,
              my: 3,
            }}
          >
            <Typography
              component="h2"
              variant="subtitle2"
              fontWeight={800}
              sx={{ mb: 2 }}
            >
              گالری تصاویر
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              {galleryImages.map((img, idx) => (
                <Box
                  key={idx}
                  component="img"
                  src={import.meta.env.VITE_URL + img}
                  alt={`تصویر کوچک ${idx + 1} ${coachName}`}
                  onClick={() => handleOpenLightbox(idx)}
                  sx={{
                    width: { xs: 70, sm: 80, md: 90 },
                    height: "auto",
                    aspectRatio: "1/1",
                    objectFit: "cover",
                    borderRadius: 2,
                    cursor: "pointer",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      transform: "scale(1.05)",
                      boxShadow: `0 4px 12px ${theme.palette.secondary.main}60`,
                    },
                  }}
                />
              ))}
            </Box>
          </Paper>
        )}

        {/* Specs & Areas Section */}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Specialties */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              component="section"
              sx={{
                p: 2,
                height: "100%",
                border: `1px solid ${theme.palette.primary.dark}90`,
                bgcolor: "rgba(92, 107, 192, 0.03)",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
              >
                <FitnessCenter color="primary" fontSize="small" />
                <Typography component="h2" variant="subtitle2" fontWeight={800}>
                  تخصص‌ها
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {specialties.map((item, idx) => (
                  <Chip
                    key={idx}
                    label={item?.name || item}
                    size="small"
                    sx={{
                      bgcolor: "primary.main",
                      color: "#fff",
                      fontWeight: 600,
                      borderRadius: 2,
                    }}
                  />
                ))}
              </Box>
            </Paper>
          </Grid>

          {/* Areas */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              component="section"
              sx={{
                p: 2,
                height: "100%",
                border: `1px solid ${theme.palette.secondary.main}30`,
                bgcolor: "rgba(0, 229, 255, 0.03)",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}
              >
                <LocationOn color="secondary" fontSize="small" />
                <Typography component="h2" variant="subtitle2" fontWeight={800}>
                  مناطق فعالیت
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {coach?.areas?.length > 0 ? (
                  coach.areas.map((area, idx) => (
                    <Chip
                      key={idx}
                      label={area?.name || area}
                      size="small"
                      variant="outlined"
                      color="secondary"
                      sx={{ fontWeight: 600, borderRadius: 2 }}
                    />
                  ))
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    نامشخص
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Certifications Section */}
        {certifications?.length > 0 && (
          <Paper
            component="section"
            sx={{
              p: { xs: 2, md: 2.5 },
              mt: 3,
              border: `1px solid rgba(255,255,255,0.05)`,
            }}
          >
            <Typography
              component="h2"
              variant="subtitle1"
              fontWeight={800}
              sx={{ mb: 2 }}
            >
              جوایز و گواهینامه‌ها
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {certifications.map((cert, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.5,
                    bgcolor: "background.default",
                    borderRadius: 2,
                    borderRight: `3px solid ${theme.palette.success.main}`,
                  }}
                >
                  <School sx={{ color: "success.main", fontSize: 20 }} />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="text.primary"
                  >
                    {cert}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        )}

        {/* Gyms Section (System Gyms + Custom Gyms) */}
        {totalGyms > 0 && (
          <Paper
            component="section"
            sx={{
              p: { xs: 2, md: 2.5 },
              mt: 3,
              border: `1px solid rgba(255,255,255,0.05)`,
            }}
          >
            <Typography
              component="h2"
              variant="subtitle1"
              fontWeight={800}
              sx={{ mb: 2 }}
            >
              باشگاه‌های محل فعالیت
            </Typography>
            <Grid container spacing={2}>
              {/* باشگاه‌های ثبت شده در سیستم */}
              {coach?.gymId?.map((gym, idx) => (
                <Grid size={{ xs: 12, sm: 6 }} key={`sys-${idx}`}>
                  <Box
                    onClick={() =>
                      navigate(
                        `/gyms/${gym?._id}/${gym?.name?.replaceAll(" ", "-")}`,
                      )
                    }
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      p: 1,
                      bgcolor: "background.default",
                      borderRadius: 3,
                      alignItems: "center",
                      border: "1px solid rgba(255,255,255,0.03)",
                    }}
                  >
                    <Avatar
                      src={gym?.coverImage}
                      alt={gym?.fullName || gym?.name}
                      variant="rounded"
                      sx={{ width: 50, height: 50, borderRadius: 2 }}
                    />
                    <Box>
                      <Typography variant="body2" fontWeight={800}>
                        {gym?.fullName || gym?.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "flex", alignItems: "center", mt: 0.25 }}
                      >
                        <LocationOn
                          sx={{
                            fontSize: 12,
                            mr: 0.5,
                            color: "secondary.main",
                          }}
                        />
                        مشاهده پروفایل باشگاه
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}

              {/* باشگاه‌های دستی (Custom Gym Names) */}
              {coach?.customGymNames?.map((gymName, idx) => (
                <Grid size={{ xs: 12, sm: 6 }} key={`custom-${idx}`}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      p: 1,
                      bgcolor: "background.default",
                      borderRadius: 3,
                      alignItems: "center",
                      border: "1px solid rgba(255,255,255,0.03)",
                    }}
                  >
                    <Avatar
                      variant="rounded"
                      sx={{
                        width: 50,
                        height: 50,
                        borderRadius: 2,
                        bgcolor: "rgba(255, 255, 255, 0.05)",
                      }}
                    >
                      <Storefront sx={{ color: "text.secondary" }} />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={800}>
                        {gymName}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "flex", alignItems: "center", mt: 0.25 }}
                      >
                        <Verified
                          sx={{ fontSize: 12, ml: 0.5, color: "primary.main" }}
                        />
                        ثبت نام نشده
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {/* Pricing Section */}
        {coach?.pricing?.length > 0 && (
          <Paper
            component="section"
            sx={{
              p: { xs: 2, md: 2.5 },
              mt: 3,
              border: `1px solid ${theme.palette.primary.main}50`,
              background:
                "linear-gradient(145deg, rgba(30,30,30,1) 0%, rgba(40,45,60,1) 100%)",
            }}
          >
            <Typography
              component="h2"
              variant="subtitle1"
              fontWeight={800}
              sx={{ mb: 2, color: "primary.light" }}
            >
              تعرفه‌های تمرین
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {coach.pricing.map((item, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: { xs: "start", lg: "center" },
                    p: 1.5,
                    bgcolor: "rgba(0,0,0,0.3)",
                    borderRadius: 2,
                    border: "1px solid rgba(255,255,255,0.05)",
                    gap: "1rem",
                    flexDirection: { xs: "column", lg: "row" },
                  }}
                >
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Verified
                        sx={{ color: "secondary.main", fontSize: "1rem" }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontSize: { xs: ".8rem", lg: "1rem" } }}
                        fontWeight={600}
                      >
                        {item?.title}
                      </Typography>
                    </Box>
                    {/* نمایش نوع تعرفه به فارسی */}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        pl: 3.5,
                      }}
                    >
                      <ClassIcon sx={{ fontSize: 14 }} />
                      نوع تمرین: {priceTypeMap[item?.type] || "سایر"}
                    </Typography>
                  </Box>

                  <Typography
                    fontWeight={800}
                    color="primary.light"
                    sx={{
                      fontSize: "0.95rem",
                      textAlign: { xs: "center", lg: "right" },
                      whiteSpace: "nowrap",
                      width: { xs: "100%", lg: "auto" },
                    }}
                  >
                    {item?.price?.toLocaleString()}{" "}
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                    >
                      تومان
                    </Typography>
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        )}

        {/* Comments Section */}
        <Box component="section" sx={{ mt: 3 }}>
          <CommentsSection targetId={coachId} targetType="Coach" />
        </Box>
      </motion.div>

      {/* Lightbox Modal */}
      <Dialog
        open={lightboxOpen}
        onClose={handleCloseLightbox}
        maxWidth="lg"
        fullWidth
        keepMounted
        sx={{ zIndex: "100000000" }}
        PaperProps={{
          sx: {
            bgcolor: "rgba(0, 0, 0, 0.44)",
            boxShadow: "none",
            overflow: "hidden",
            height: "100%",
            m: { xs: 0, md: 4 },
            borderRadius: { xs: 0, md: 4 },
          },
        }}
      >
        <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
          <IconButton
            onClick={handleCloseLightbox}
            sx={{
              position: "absolute",
              top: { xs: 16, md: 24 },
              right: { xs: 16, md: 24 },
              color: "#fff",
              bgcolor: "rgba(255,255,255,0.1)",
              zIndex: 50,
              "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
            }}
          >
            <Close />
          </IconButton>

          {lightboxOpen && (
            <Swiper
              modules={[Navigation, Pagination, Zoom]}
              navigation
              pagination={{ clickable: true, dynamicBullets: true }}
              zoom={true}
              initialSlide={initialSlide}
              spaceBetween={30}
              slidesPerView={1}
              style={{
                width: "100%",
                height: "100%",
                "--swiper-navigation-color": theme.palette.secondary.main,
                "--swiper-pagination-color": theme.palette.secondary.main,
              }}
            >
              {galleryImages.map((img, idx) => (
                <SwiperSlide
                  key={idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div className="swiper-zoom-container">
                    <img
                      src={import.meta.env.VITE_URL + img}
                      alt={`گالری ${idx + 1}`}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "85vh",
                        objectFit: "cover",
                        borderRadius: "8px",
                        aspectRatio: "1/1",
                      }}
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          )}
        </Box>
      </Dialog>
    </Box>
  );
}
